/*
IMPORTANT:
Transition is not full yet, some functions still rely on their context being the api object
*/

import { 
    FileSystem as FS, 
    Logger as logger
} from '../../utils';
import { dataSource } from '../../Functions';
import * as GlobalTypes from "../../types";
import { ActuatorCatalog, ActuatorFamily, ActuatorFamilyFixed } from '../documents/ActuatorCatalog';
import { BlowerCatalog, BlowerFamily } from '../documents/BlowerCatalog';
import { ChloirneSensorGains, ChlorineSensorCatalog, ChlorineSensorCatalogItem } from '../documents/ChlorineSensorCatalog';
import { DissolvedOxygenSensorCatalog, DissolvedOxygenSensorCatalogItem } from '../documents/DissolvedOxygenSensorCatalog';
import { FlowMeterCatalog, FlowMeterFamily } from '../documents/FlowMeterCatalog';
import { HeaterCatalog, HeaterDeviceTypes, HeaterFamily } from '../documents/HeaterCatalog';
import { LightCatalog, LightFamily, LightInfo, LightInfoFlat, LightInfoColorAndShowLibrary, LightColorAndShowLibraryPortTypes } from '../documents/LightCatalog';
import { LightDriverCatalog, LightDriverFamily } from '../documents/LightDriverCatalog';
import { PumpCatalog, PumpFamily } from '../documents/PumpCatalog';
import { SwgCatalog, SwgFamily, SwgInfo, SwgInfoFlat } from '../documents/SwgCatalog';
import { RemoteCatalog, RemoteFamily } from '../documents/RemoteCatalog';
import { CatalogRealms, AllCatalogs, CatalogFileNames } from "../Types";
import { SiteRecord, DeviceConfig, PortTypeValues, DeviceTypes, LightSettings } from '../../config';
import { default as LocalCatalogs } from '../Locals';
import * as FileSystem from '../../utils/FileSystem';
import Locale from "../../utils/Locale";
import { PressureSensorCatalog, PressureSensor } from '../documents/PressureSensorCatalog';
import { CatalogItemTaints } from '../documents/common';

export * from './getCatalogTranslation';


enum targetTypes { "boolean", "number" };

const CatalogFileKeyMap = {
    [ CatalogFileNames.ActuatorCatalog ]: "Actuator",
    [ CatalogFileNames.BlowerCatalog ]: "Blower",
    [ CatalogFileNames.ChlorineSensorCatalog ]: "ChlorineSensor",
    [ CatalogFileNames.DissolvedOxygenSensorCatalog ]: "DissolvedOxygenSensor",
    [ CatalogFileNames.FlowMeterCatalog ]: "FlowMeter",
    [ CatalogFileNames.HeaterCatalog ]: "Heater",
    [ CatalogFileNames.LightCatalog ]: "Light",
    [ CatalogFileNames.LightDriverCatalog ]: "LightDriver",
    [ CatalogFileNames.PumpCatalog ]: "Pump",
    [ CatalogFileNames.PressureSensorCatalog ]: "PressureSensor",
    [ CatalogFileNames.RemoteCatalog ]: "Remote",
    [ CatalogFileNames.SwgCatalog ]: "SaltChlorinator",
};
const staticDataBaseUrl = "https://poolside-catalog-assets.s3.amazonaws.com/[[RUNMODE]]/json";

let realm:CatalogRealms = 'prod';
export function setCatalogRealm(newRealm:CatalogRealms) {
    realm = newRealm;
}

export const getLocalCatalogs = () => LocalCatalogs;

/**
 * @param {string} fileName
 * @param {number} [cacheValiditySecs=0] Number of seconds while it is ok to fall back to local cache without throwing an error if it is not possible to check etag header or downloading file is failed.
 * @returns {object}
 * @throws {Error} Throws according to mode selected by `cacheValiditySecs` param.
 */
export const checkDataFile = async function(fileName, cacheValiditySecs = 0) {
    let FileSystem = FS.getFileSystemTool();
    const now = new Date();
    
    const fileNameLocal = fileName.replace("/", "_") + '.' + realm;
    const filePath = FileSystem.documentDirectory + fileNameLocal;
    const metaDataFilePath = filePath + '.info';
    let fileInfo;

    try {
        fileInfo = await FileSystem.getInfoAsync(filePath, {
            md5: true
        });
        fileInfo.validityLastChecked = 0;
        if (fileInfo.exists) {
            try {
                const validityLastChecked = await FileSystem.readAsStringAsync(metaDataFilePath);
                if (validityLastChecked) {
                    fileInfo.validityLastChecked = (new Date(validityLastChecked)).getTime();
                }
                logger.debug('checkDataFile() meta:', metaDataFilePath, validityLastChecked);
            } catch (err) {
                await FileSystem.writeAsStringAsync(metaDataFilePath, now.toISOString());
                logger.error('Can\'t read meta data file: ' + metaDataFilePath, err?.message ? err.message : err);
            }
        }
    } catch (e) {
        logger.error(`checkDataFile() Can't get file info for file: ${filePath}:`, e)
        fileInfo = {
            exists: false,
            modificationTime: 0,
            validityLastChecked: 0
        };
    }

    const baseUrl = staticDataBaseUrl.replace('[[RUNMODE]]', realm);
    const url = baseUrl + '/' + fileName;

    if (!fileInfo.exists || cacheValiditySecs > 0) {
        try {   
            logger.debug('checkDataFile() Getting etag for from:', url);
            const headResponse = await fetch(url, {
                method: 'HEAD'
            });
            // .replaceAll() did not work on Android
            const headEtag = headResponse.headers.has('etag') && headResponse.headers.get('etag').replace(/"/g, '');
            logger.debug('checkDataFile() Got etag: ' + headEtag + ' from:', url);
            if (!headEtag || !fileInfo.md5 || (headEtag != fileInfo.md5)) {
                logger.info('checkDataFile() Downloading:', fileName, ' from:', url);
                const res = await FileSystem.downloadAsync(url, filePath + '.tmp', {
                    md5: true,
                    cache: true
                });
                if (res.status !== 200) {
                    throw Error(`checkDataFile() Unexpected HTTP response code: ${res.status}, url: ${url}`);
                } else {
                    await FileSystem.moveAsync({ from: filePath + '.tmp', to: filePath });
                    logger.debug('checkDataFile() Downloaded: ' + fileName + ' as: ' + filePath);
                    //this.isCacheUpdated.catalogs[fileName] = true;
                }
            } else {
                logger.info("checkDataFile() Header ETag matches current MD5 sum for: " + url);
            }
            logger.debug("checkDataFile() bumping date in the meta data file: " + metaDataFilePath + ' to: ' + now);
            await FileSystem.writeAsStringAsync(metaDataFilePath, now.toISOString());
        } catch (err) {
            logger.debug("checkDataFile() Could not get ETag header or download file: " + url, err);
            // Can't check etag header or could not download file
            if (cacheValiditySecs > 0 && fileInfo.exists &&
                (fileInfo.validityLastChecked > (now.getTime() / 1000) - cacheValiditySecs)
            ) {
                logger.info("checkDataFile() Falling back to local cache with time: " + fileInfo.modificationTime + ", validityLastChecked:" + fileInfo.validityLastChecked + " of file: " + url);
            } else {
                const tmp = 'Could not fall back to using cache. Cache time: ' + fileInfo.modificationTime + ', cache validity last checked: ' + fileInfo.validityLastChecked + ', current time: ' + now.getTime() / 1000 + ', cacheValiditySecs: ' + cacheValiditySecs + ', file: ' + url;
                logger.debug('checkDataFile() ' + tmp);
                if (err.message) {
                    err.message += ' ' + tmp;
                } else {
                    err.message = tmp;
                }
                throw err;
            }
        }
    } else {
        logger.info("checkDataFile() Found file locally with time " + fileInfo.modificationTime + " of file: " + url + " cacheValiditySecs:" + cacheValiditySecs);
    }
        
    let fileDataRaw = await FileSystem.readAsStringAsync(filePath);

    try {
        return JSON.parse(fileDataRaw);
    } catch (err) {
        throw Error(`Could not JSON decode file: ${fileNameLocal}: ${err}`);
    }
}

export const clearCatalogCache = async function () {
    let FileSystem = FS.getFileSystemTool();
    for (let catalogFileName of Object.keys(this.catalogData)) {
        for (let version of Object.keys(this.catalogData[catalogFileName])) {
            let fileName = 'v' + version + '/' + catalogFileName;
            logger.info('Clearing catalog cache file: ' + fileName);
            
            const fileNameLocal = fileName.replace("/", "_") + '.' + realm;
            const filePath = FileSystem.documentDirectory + fileNameLocal;
            const metaDataFilePath = filePath + '.info';
            let fileInfo;
            try {
                fileInfo = await FileSystem.getInfoAsync(filePath, {});
                if (fileInfo.exists) {
                    logger.info('Deleting file: ' + filePath);
                    await FileSystem.deleteAsync(filePath);
                }
                fileInfo = await FileSystem.getInfoAsync(metaDataFilePath, {});
                if (fileInfo.exists) {
                    logger.info('Deleting file: ' + metaDataFilePath);
                    await FileSystem.deleteAsync(metaDataFilePath);
                }
            } catch (e) {
                logger.error('Error while clearing catalog cache: ' + e.message)
            }
        }
    }
}

/**
 * @param {number} [cacheValiditySecs=0] Number of seconds while it is ok to fall back to local cache without throwing n error if it is not possible to check etag header or downloading file has failed. 0 = "if cache exists, use that"
 * @param {boolean} [force=false] Whether to force the check for new catalogs
 */
export const checkCatalogs = async function(cacheValiditySecs = 0, force = false) {
    let versionsRequired = [];
    for (let site of this.siteList) {
        let reqVer = 1;
        let siteStates = this.getSiteStates(true, site.uuid);
        if (!siteStates.AutoFilterVersion && new Date(site.lastSeen).getTime() > Date.now() - 30*24*60*60*1000) {
            logger.info("Updating states for " + site.uuid + " / " + site.name + " to be able to determine catalog version");
            await this.updateStates(site.id);
            siteStates = this.getSiteStates(true, site.uuid);
        }
        if (Number(siteStates.AutoFilterVersion || 0) >= 6) {
            if (!isNaN(Number(siteStates.CatalogVersion))) {
                reqVer = Number(siteStates.CatalogVersion);
            } else {
                logger.warn("Site " + site.uuid + " / " + site.name + " does not have a valid CatalogVersion state: " + siteStates.CatalogVersion);
            }
        }
        if (versionsRequired.indexOf(reqVer) === -1)
            versionsRequired.push(reqVer);
    }
    logger.info('checkCatalogs() cacheValiditySecs:', cacheValiditySecs, 'sites:', this.siteList.length, 'versions:', versionsRequired);

    for (let version of versionsRequired) {
        try {
            await CatalogStorage.autoLoad(version);
        } catch(err) {
            logger.error("checkCatalog() could not autoload catalog version:", version, err);
        }
    }

    if (!this.faqData || cacheValiditySecs > 0) {
        try {
            let faqJson = await this.checkDataFile('Faq.json', cacheValiditySecs);
            this.faqData = faqJson;
        } catch (err) {
            logger.error("Error downloading FAQ document");
        }
    }
}

export const getChlorineSensorCatalog = function(site?:SiteRecord): ChlorineSensorCatalogItem[] {
    return getCatalog(CatalogFileNames.ChlorineSensorCatalog, site) as ChlorineSensorCatalogItem[];
}

export const getDissolvedOxygenSensorCatalog = function(site?: SiteRecord): DissolvedOxygenSensorCatalogItem[] {
    return getCatalog(CatalogFileNames.DissolvedOxygenSensorCatalog, site) as DissolvedOxygenSensorCatalogItem[];
}

export const getLightCatalog = function(site?:SiteRecord): LightFamily[] {
    return getCatalog(CatalogFileNames.LightCatalog, site) as LightFamily[];
}

export const getLightCatalogRaw = function(site?:SiteRecord): LightCatalog {
    return getCatalog(CatalogFileNames.LightCatalog, site, true) as LightCatalog;
}

export const getLightDriverCatalog = function (site?:SiteRecord):LightDriverFamily[] {
    return getCatalog(CatalogFileNames.LightDriverCatalog, site) as LightDriverFamily[];
}

export const getPressureSensorCatalog = function (site?:SiteRecord): PressureSensor[] {
    return getCatalog(CatalogFileNames.PressureSensorCatalog, site) as PressureSensor[];
}

export const getPumpCatalog = function(site?:SiteRecord, CatalogVersion?: number): PumpFamily[] {
    return getCatalog(CatalogFileNames.PumpCatalog, site, undefined, CatalogVersion) as PumpFamily[];
}

export const getHeaterCatalog = function (site?:SiteRecord): HeaterFamily[] {
    return getCatalog(CatalogFileNames.HeaterCatalog, site) as HeaterFamily[];
}

export const getFlowMeterCatalog = function (site?:SiteRecord):FlowMeterFamily[] {
    return getCatalog(CatalogFileNames.FlowMeterCatalog, site) as FlowMeterFamily[];
}

export const getActuatorCatalog = function (site?:SiteRecord): ActuatorFamily[] {
    return getCatalog(CatalogFileNames.ActuatorCatalog, site) as ActuatorFamily[];
}

export const getSaltCellCatalog = function (site?:SiteRecord):SwgFamily[] {
    return getCatalog(CatalogFileNames.SwgCatalog, site) as SwgFamily[];
}

export const getBlowerCatalog = function (site?:SiteRecord):BlowerFamily[] {
    return getCatalog(CatalogFileNames.BlowerCatalog, site) as BlowerFamily[];
}

export const getRemoteCatalog = function (site?:SiteRecord):RemoteFamily[] {
    return getCatalog(CatalogFileNames.RemoteCatalog, site) as RemoteFamily[];
}

/**
 * Adds portTypesAvailable prop to the catalog entry in case it does not have one.
 * Starting by Catalog v32 prop portTypesAvailable was added to the catalog.
 * Pre v32 catalogs had controlType only.
 * @param catalogItem 
 * @returns 
 */
export function addPortTypesAvailable(catalogItem: object, deviceType: DeviceTypes): object {
    const catalogItemCopy = JSON.parse(JSON.stringify(catalogItem || {}) || "{}");

    if (!catalogItemCopy.portTypesAvailable) {
        catalogItemCopy.portTypesAvailable = [];

        // Blower catalog does not contain portType nor contronlType
        if (deviceType === DeviceTypes.BLOWER) {
            catalogItemCopy.portTypesAvailable.push(PortTypeValues.HighVoltage);
        }

        switch (catalogItemCopy.portType) {
        case LightColorAndShowLibraryPortTypes.Relay:
            catalogItemCopy.portTypesAvailable.push(PortTypeValues.HighVoltage);
            break;
        case LightColorAndShowLibraryPortTypes.Serial:
            catalogItemCopy.portTypesAvailable.push(PortTypeValues.Serial);
            break;
        }

        switch (catalogItemCopy.controlType) {
        case GlobalTypes.ControlTypes.VALVE:
            catalogItemCopy.portTypesAvailable.push(PortTypeValues.Actuator);
            break;
        case GlobalTypes.ControlTypes.BOTH:
            catalogItemCopy.portTypesAvailable.push(PortTypeValues.Serial);
            catalogItemCopy.portTypesAvailable.push(PortTypeValues.HighVoltage);
            if ([ DeviceTypes.HEATER, DeviceTypes.HEATPUMP ].includes(deviceType)) {
                catalogItemCopy.portTypesAvailable.push(PortTypeValues.LowVoltage);
            }
            break;
        case GlobalTypes.ControlTypes.BUS:
            catalogItemCopy.portTypesAvailable.push(PortTypeValues.Serial);
            break;
        case GlobalTypes.ControlTypes.RELAY:
            catalogItemCopy.portTypesAvailable.push(PortTypeValues.HighVoltage);
            if ([ DeviceTypes.HEATER, DeviceTypes.PUMP, DeviceTypes.ACTUATORTWOWAY, DeviceTypes.ACTUATORTHREEWAY ].includes(deviceType)) {
                catalogItemCopy.portTypesAvailable.push(PortTypeValues.LowVoltage);
            }
            break;
        case GlobalTypes.ControlTypes.SERIAL:
            catalogItemCopy.portTypesAvailable.push(PortTypeValues.Serial);
            break;
        case GlobalTypes.ControlTypes.mA:
            catalogItemCopy.portTypesAvailable.push(PortTypeValues.Chemistry420);
            break;
        }
    }

    return catalogItemCopy;
}

const catalogIndex = {};
export const getCatalogIndexed = function (fileName: CatalogFileNames, site?:SiteRecord, key:string = "itemId", CatalogVersion?: number):any {
    let version: number;
    if (CatalogVersion) {
        version = CatalogVersion;
    } else {
        version = getSiteCatalogVersion(fileName, site);
    }
    
    if (!catalogIndex[version]) {
        catalogIndex[version] = {};
    }
    if (!catalogIndex[version][fileName]) {
        catalogIndex[version][fileName] = {};
    }
    if (!catalogIndex[version][fileName][key]) {
        const catArr = getCatalogFile(fileName, version, true) as (
            ActuatorFamily[] | DissolvedOxygenSensorCatalogItem[] | FlowMeterFamily[] | HeaterFamily[] | LightFamily[] | LightInfoFlat[] | LightDriverFamily[] | PressureSensor[] | PumpFamily[] | SwgFamily[] | ChlorineSensorCatalogItem[]
        );
        const indexedCatalog = {};
        for (const item of catArr) {
            indexedCatalog[item[key]] = item;
        }
        catalogIndex[version][fileName][key] = indexedCatalog;
    }

    return catalogIndex[version][fileName][key];
}

export const getSiteCatalogVersion = function(fileName: string, site?:SiteRecord) {
    let version = CatalogStorage.getLatestVersionNumber();

    site = site || dataSource?.getSelectedSite();
    if (site && dataSource) {
        let siteStates = dataSource.getSiteStates(true, site.uuid);
        if (Number(siteStates.AutoFilterVersion || 0) >= 6 && siteStates.CatalogVersion) {
            version = !isNaN(Number(siteStates.CatalogVersion)) ? Number(siteStates.CatalogVersion) : 2;
        }
    }
    // Forces light catalog version to a minimum of: 8
    if (fileName === CatalogFileNames.LightCatalog) {
        version = Math.max(8, version);
    }

    return version;
}

/**
 * Automatically selects catalog version and returns data of that catalog.
 * If it cannot resolve the version from the site, it will use the latest
 * @param fileName Catalog file name e.g: LightCatalog.json
 * @param [site]
 * @param [raw] Set to true for getting the catalog file in it's original format.
 * Returns only the array of catalog items otherwise.
 */
export const getCatalog = function (fileName: CatalogFileNames, site?:SiteRecord, raw:boolean = false, CatalogVersion?: number):
    // raw: false
    ActuatorFamily[] | DissolvedOxygenSensorCatalogItem[] | FlowMeterFamily[] | HeaterFamily[] | LightFamily[] | LightInfoFlat[] | LightDriverFamily[] | PressureSensor[] | PumpFamily[] | SwgFamily[] | ChlorineSensorCatalogItem[]
    
    // raw: true
    | ActuatorCatalog | DissolvedOxygenSensorCatalog | FlowMeterCatalog | HeaterCatalog | LightCatalog | LightInfo | LightDriverCatalog | PressureSensorCatalog | PumpCatalog | SwgCatalog | ChlorineSensorCatalog
{
    let version: number;
    if (CatalogVersion) {
        version = CatalogVersion;
    } else {
        version = getSiteCatalogVersion(fileName, site);
    }
    // console.log("CATALOGVERSION", version);
    return getCatalogFile(fileName, version, true, raw);
}

export const getCatalogFile = function (fileName: CatalogFileNames, versionNum = 1, logError: boolean = true, raw: boolean = false):
    // raw: false
    ActuatorFamily[] | DissolvedOxygenSensorCatalogItem[] | FlowMeterFamily[] | HeaterFamily[] | LightFamily[] | LightInfoFlat[] | LightDriverFamily[] | PressureSensor[] | PumpFamily[] | SwgFamily[] | ChlorineSensorCatalogItem[]
    
    // raw: true
    | ActuatorCatalog | DissolvedOxygenSensorCatalog | FlowMeterCatalog | HeaterCatalog | LightCatalog | LightInfo | LightDriverCatalog | PressureSensorCatalog | PumpCatalog | SwgCatalog | ChlorineSensorCatalog

{
    // TODO missing await
    const catalogs = CatalogStorage.getVersion(versionNum);
    if (!catalogs) {
        if (logError) {
            logger.error('getCatalogFile() Cannot find version ' + versionNum + ' of catalog collection for ' + fileName);
        }
        return (raw ?
            ({} as ActuatorCatalog | DissolvedOxygenSensorCatalog | FlowMeterCatalog | HeaterCatalog | LightCatalog | LightInfo | LightDriverCatalog | PressureSensorCatalog | PumpCatalog | SwgCatalog | ChlorineSensorCatalog) :
            []
        );
    }

    const catalog = catalogs[ CatalogFileKeyMap[ fileName ] ];
    if (!catalog) {
        if (logError) {
            logger.error('getCatalogFile() Cannot find: ' + fileName + ' in catalog collection v' + versionNum);
        }
        return (raw ?
            ({} as ActuatorCatalog | DissolvedOxygenSensorCatalog | FlowMeterCatalog | HeaterCatalog | LightCatalog | LightInfo | LightDriverCatalog | PressureSensorCatalog | PumpCatalog | SwgCatalog | ChlorineSensorCatalog) :
            []
        );
    }

    if (raw) {
        logger.verbose("getCatalogFile() [raw] Fetched catalog from local: " + fileName);
        return catalog;
    } else {
        const {
            timestamp, version,
            ...catRemains
        } = catalog;

        if (Array.isArray(Object.values(catRemains)[0])) {
            logger.verbose("getCatalogFile() Fetched catalog from local: " + fileName);
            return Object.values(catRemains)[0] as any[];
        } else if (logError) {
            logger.error('getCatalogFile() Can not found array in: ' + fileName + ' in catalog collection v' + versionNum);
        }
    }

    return (raw ? 
        ({} as ActuatorCatalog | DissolvedOxygenSensorCatalog | FlowMeterCatalog | HeaterCatalog | LightCatalog | LightInfo | LightDriverCatalog | PressureSensorCatalog | PumpCatalog | SwgCatalog | ChlorineSensorCatalog) :
        []
    );
}

export const getCatalogByDeviceType = function(deviceType: DeviceTypes, site: SiteRecord):
    ActuatorFamily[] | DissolvedOxygenSensorCatalogItem[] | FlowMeterFamily[] | HeaterFamily[] | LightFamily[] | LightInfoFlat[] | LightDriverFamily[] | PressureSensor[] | PumpFamily[] | SwgFamily[] | ChlorineSensorCatalogItem[]
{
    switch(deviceType) {
    case DeviceTypes.ACTUATORTHREEWAY:
    case DeviceTypes.ACTUATORTWOWAY:
        return getActuatorCatalog(site);
    case DeviceTypes.CHLORINESENSOR:
        return getChlorineSensorCatalog(site);    
    case DeviceTypes.DISSOLVEDOXYGENSENSOR:
        return getDissolvedOxygenSensorCatalog(site);    
    case DeviceTypes.FLOWMETER:
        return getFlowMeterCatalog(site);
    case DeviceTypes.CHILLER:
        return getHeaterCatalog(site)?.filter(item => item.deviceType === HeaterDeviceTypes.CHILLER);
    case DeviceTypes.HEATER:
        return getHeaterCatalog(site)?.filter(item => item.deviceType === HeaterDeviceTypes.HEATER);
    case DeviceTypes.HEATPUMP:
        return getHeaterCatalog(site)?.filter(item => item.deviceType === HeaterDeviceTypes.HEATPUMP);
    case DeviceTypes.LIGHT:
        return getLightCatalog(site);
    case DeviceTypes.LIGHTDRIVER:
        return getLightDriverCatalog(site);
    case DeviceTypes.PRESSURESENSOR:
        return getPressureSensorCatalog(site);
    case DeviceTypes.PUMP:
        return getPumpCatalog(site);
    case DeviceTypes.REMOTE:
        return getRemoteCatalog(site);
    case DeviceTypes.SALTCELL:
        return getSaltCellCatalog(site);
    default:
        logger.debug("[Catalog] getCatalogByDeviceType() not supported device type:", deviceType);
    }
}


/**
 * Common function for returning catalog entry for various kind of devices.
 * Mocked catalog entry will be returned for Devices that have no catalog in order to provide `portTypesAvailable` for them.
 * @param deviceConfig
 * @returns Catalog entry based on the type: CatalogEntry.
 */
export const getCatalogEntryByDeviceConfig = function(deviceConfig: DeviceConfig):
    ActuatorFamilyFixed | FlowMeterFamily | HeaterFamily | PressureSensor | PumpFamily | LightInfoFlat | LightDriverFamily | RemoteFamily | SwgInfoFlat | ChlorineSensorCatalogItem | { brand: string, portTypesAvailable: PortTypeValues[], kbUrl?: string }
{
switch(deviceConfig?.DeviceType) {
    case DeviceTypes.ACTUATORTHREEWAY:
    case DeviceTypes.ACTUATORTWOWAY:
        return getActuatorCatalogEntryFixed(deviceConfig?.HardwareDevice?.PrimaryCatalogId);
    case DeviceTypes.AUTOFILL:
        return ({
            brand: deviceConfig.HardwareDevice?.Make,
            kbUrl: "https://poolside.support/kb/autofill/",
            portTypesAvailable: [
                PortTypeValues.Switch,
                PortTypeValues.LowVoltage,
            ]
        });
    case DeviceTypes.BLOWER:
        return getBlowerInfo(deviceConfig?.HardwareDevice?.PrimaryCatalogId);
    case DeviceTypes.BUTTON:
        return ({
            brand: deviceConfig.HardwareDevice?.Make,
            kbUrl: "https://poolside.support/kb/button-remotes/",
            portTypesAvailable: [ PortTypeValues.Switch ]
        });
    case DeviceTypes.CHLORINESENSOR:
        return getChlorineSensorCatalogEntry(deviceConfig?.HardwareDevice?.PrimaryCatalogId);
    case DeviceTypes.COVER:
        return ({
            brand: deviceConfig.HardwareDevice?.Make,
            kbUrl: "https://poolside.support/kb/pool-spa-cover-sensors/",
            portTypesAvailable: [ PortTypeValues.Switch ]
        })
    case DeviceTypes.FILLSWITCH:
    case DeviceTypes.FLOWSWITCH:
        return ({
            brand: deviceConfig.HardwareDevice?.Make,
            kbUrl: "https://poolside.support/kb/flow-switches/",
            portTypesAvailable: [ PortTypeValues.Switch ]
        });
    case DeviceTypes.CHEMICALPUMP:
        return ({
            brand: deviceConfig.HardwareDevice?.Make,
            kbUrl: "https://poolside.support/kb/how-to-set-up-chemical-feeders-on-the-attendant/",
            portTypesAvailable: [
                PortTypeValues.HighVoltage,
                PortTypeValues.LowVoltage,
            ]
        });
    case DeviceTypes.FLOWMETER:
        return getFlowMeterInfo(deviceConfig?.HardwareDevice?.PrimaryCatalogId);
    case DeviceTypes.FLOWRELAY:
        return ({
            brand: deviceConfig.HardwareDevice?.Make,
            portTypesAvailable: [
                PortTypeValues.HighVoltage,
                PortTypeValues.LowVoltage,
            ]
        });
    case DeviceTypes.CHILLER:
    case DeviceTypes.HEATER:
    case DeviceTypes.HEATPUMP:
        return getHeaterInfo(deviceConfig?.HardwareDevice?.PrimaryCatalogId);
    case DeviceTypes.LIGHT:
        return getLightInfoFlat(deviceConfig?.HardwareDevice?.LightSettings) as LightInfoFlat;
    case DeviceTypes.LIGHTDRIVER:
        return getLightDriverInfo(deviceConfig?.HardwareDevice?.PrimaryCatalogId) as LightDriverFamily;
    case DeviceTypes.PUMP:
        return getPumpInfo(deviceConfig?.HardwareDevice?.PrimaryCatalogId);
    case DeviceTypes.REMOTE:
        return getRemoteInfo(deviceConfig?.HardwareDevice?.PrimaryCatalogId);
    case DeviceTypes.PRESSURESENSOR:
        return getPressureSensorInfo(deviceConfig?.HardwareDevice?.PrimaryCatalogId);
    case DeviceTypes.SALTCELL:
        return getSaltCellCatalogEntryFlat(
            deviceConfig.HardwareDevice?.PrimaryCatalogId,
            deviceConfig.HardwareDevice?.SecondaryCatalogId
        );
    case DeviceTypes.THERMISTOR:
        return ({
            brand: deviceConfig.HardwareDevice?.Make,
            kbUrl: "https://poolside.support/kb/temperature-sensors-thermistors/",
            portTypesAvailable: [ PortTypeValues.Thermistor ]
        });
    case DeviceTypes.FIRE:
        return ({
            brand: deviceConfig.HardwareDevice?.Make,
            kbUrl: "https://poolside.support/kb/single-speed-pumps/",
            portTypesAvailable: [ PortTypeValues.HighVoltage ]
        });
    case DeviceTypes.GENERICONOFF:
        return ({
            brand: deviceConfig.HardwareDevice?.Make,
            kbUrl: "https://poolside.support/kb/single-speed-pumps/",
            portTypesAvailable: [
                PortTypeValues.HighVoltage,
                PortTypeValues.LowVoltage,
            ]
        });
    case DeviceTypes.OZONATOR:
        return ({
            brand: deviceConfig.HardwareDevice?.Make,
            portTypesAvailable: [ PortTypeValues.HighVoltage ]
        });
    case DeviceTypes.UVSANITIZER:
        return ({
            brand: deviceConfig.HardwareDevice?.Make,
            portTypesAvailable: [ PortTypeValues.HighVoltage ]
        });
    case DeviceTypes.ORPPROBE:
        return ({
            brand: deviceConfig.HardwareDevice?.Make,
            kbUrl: "https://poolside.support/kb/wiring-and-connecting-ph-and-orp-probes/",
            portTypesAvailable: [ PortTypeValues.ChemistryORP ]
        });
    case DeviceTypes.PHPROBE:
        return ({
            brand: deviceConfig.HardwareDevice?.Make,
            kbUrl: "https://poolside.support/kb/wiring-and-connecting-ph-and-orp-probes/",
            portTypesAvailable: [ PortTypeValues.ChemistryPH ]
        });
    case DeviceTypes.WATERLEVELSENSOR:
        return ({
            brand: deviceConfig.HardwareDevice?.Make,
            // This url is correct. This article has comprehensive directions for autofills, sensors, and leveling in general
            kbUrl: "https://poolside.support/kb/autofill/",
            portTypesAvailable: [
                PortTypeValues.Switch,
                PortTypeValues.Thermistor
            ]
        });
    case DeviceTypes.DISSOLVEDOXYGENSENSOR:
        return getDissolvedOxygenSensorCatalogEntry(deviceConfig?.HardwareDevice?.PrimaryCatalogId);
    }
}


export const getBlowerInfo = function(itemId: string, site?:SiteRecord): BlowerFamily {
    const indexed = getCatalogIndexed(CatalogFileNames.BlowerCatalog, site);
    
    let catalogItem: BlowerFamily;
    if (indexed && indexed[ itemId ]) {
        catalogItem = indexed[ itemId ];
    } else {
        catalogItem = getBlowerCatalog(site).find(item => itemId === item.itemId);
    }
    if (!catalogItem) {
        catalogItem = {} as BlowerFamily;
    }
    if (!catalogItem.kbUrl) {
        catalogItem.kbUrl = "https://poolside.support/kb/air-blowers";
    }
    if (catalogItem) {    
        return addPortTypesAvailable(catalogItem, DeviceTypes.BLOWER) as BlowerFamily;
    }
}


export const getFlowMeterInfo = function(itemId: string, site?:SiteRecord): FlowMeterFamily {
    const indexed = getCatalogIndexed(CatalogFileNames.FlowMeterCatalog, site);
    
    let catalogItem: FlowMeterFamily;
    if (indexed && indexed[ itemId ]) {
        catalogItem = indexed[ itemId ];
    } else {
        catalogItem = getFlowMeterCatalog(site).find(item => itemId === item.itemId);
    }
    if (catalogItem) {    
        return addPortTypesAvailable(catalogItem, DeviceTypes.FLOWMETER) as FlowMeterFamily;
    }
}

export const getPressureSensorInfo = function(itemId: string, site?:SiteRecord): PressureSensor {
    const indexed = getCatalogIndexed(CatalogFileNames.PressureSensorCatalog, site);
    
    let catalogItem: PressureSensor;
    if (indexed && indexed[ itemId ]) {
        catalogItem = indexed[ itemId ];
    } else {
        catalogItem = getPressureSensorCatalog(site).find(item => itemId === item.itemId);
    }

    return catalogItem;
}

/**
 * @param device DeviceConfig record or PrimaryCatalogId of the device
 * @return
 */
export const getPumpInfo = function(device: (string | DeviceConfig), site?:SiteRecord, CatalogVersion?: number): PumpFamily {
    const indexed = getCatalogIndexed(CatalogFileNames.PumpCatalog, site, undefined, CatalogVersion);
    const catalogId = ((device as DeviceConfig)?.HardwareDevice?.PrimaryCatalogId) || (device as string);
    
    let catalogItem: PumpFamily;
    if (indexed && indexed[catalogId]) {
        catalogItem = indexed[catalogId];
    } else {
        catalogItem = getPumpCatalog(site, CatalogVersion).find(pumpRec => catalogId === pumpRec.itemId)    
    }

    if (catalogItem) {
        return addPortTypesAvailable(catalogItem, DeviceTypes.PUMP) as PumpFamily;
    }
}

export const getRemoteInfo = function(itemId: string): RemoteFamily {
    const indexed = getCatalogIndexed(CatalogFileNames.RemoteCatalog);
    
    let catalogItem: RemoteFamily;
    if (indexed && indexed[ itemId ]) {
        catalogItem = indexed[ itemId ];
    } else {
        catalogItem = getRemoteCatalog().find(item => itemId === item.itemId);
    }
    if (catalogItem) {    
        return addPortTypesAvailable(catalogItem, DeviceTypes.REMOTE) as RemoteFamily;
    }
}

/**
 * @description Finds Light catalog record
 * @param lightSettings E.g:
 * {
 *   {String} CompatibleBrandId
 *   {String} ColorAndShowLibraryId
 *   {String} ColorAndShowSelectionMethodId
 * }
 * @return
 * E.g:
 * {
 *   id
 *   name: brand.name,
 *   colorAndShowLibrary: {
 *     id: library.id,
 *     name: library.name,
 *     isDefault: library.isDefault,
 *     colorsAndShows: library.colorsAndShows,
 *     selectionMethod: method
 *   }
 * }
 * @deprecated Use getLightInfoFlat() instead
 * @see getLightInfoFlat()
 */
export const getLightInfo = function (lightSettings: LightSettings, site?:SiteRecord): LightInfo {
    if (!lightSettings) return null;

    const catalog = getLightCatalog(site);

    const brand = catalog.find(item => item.id === lightSettings.CompatibleBrandId);
    if (!brand) return null;

    const library = brand.colorAndShowLibraries.find(item => item.id === lightSettings.ColorAndShowLibraryId);
    if (!library) return null
    
    const method = library.selectionMethods.find(item => item.id === lightSettings.ColorAndShowSelectionMethodId);

    const info = JSON.parse(
        JSON.stringify({
            id: brand.id,
            name: brand.name,
            colorAndShowLibrary: {
                ...library,
                selectionMethod: method,
            }

        })
    ) as LightInfo

    if (info.colorAndShowLibrary) {
        info.colorAndShowLibrary = addPortTypesAvailable(info.colorAndShowLibrary, DeviceTypes.LIGHT) as LightInfoColorAndShowLibrary;
    }

    return info;
}

export const getLightInfoFlat = function (lightSettings: LightSettings, site?:SiteRecord): LightInfoFlat {
    if (!lightSettings) return null;

    const catalog = getLightCatalog(site);

    const family = catalog.find(item => item.id === lightSettings.CompatibleBrandId);
    if (!family) return null;

    const colorAndShowLibrary = family.colorAndShowLibraries.find(item => item.id === lightSettings.ColorAndShowLibraryId);
    if (!colorAndShowLibrary) return null
    
    const selectionMethod = colorAndShowLibrary.selectionMethods.find(item => item.id === lightSettings.ColorAndShowSelectionMethodId);

    let info = JSON.parse(
        JSON.stringify({
            id: family.id,
            name: family.name,
            brand: family.name,
            model: colorAndShowLibrary.name,
            description: selectionMethod?.name,
            ...colorAndShowLibrary,
            selectionMethod,
        })
    ) as LightInfoFlat;

    if (info) {
        info = addPortTypesAvailable(info, DeviceTypes.LIGHT) as LightInfoFlat;
    }

    return info;
}

export const getLightDriverInfo = function(itemId: string, site?:SiteRecord): LightDriverFamily {
    const indexed = getCatalogIndexed(CatalogFileNames.LightDriverCatalog, site);
    
    let catalogItem: LightDriverFamily;
    if (indexed && indexed[ itemId ]) {
        catalogItem = indexed[ itemId ];
    } else {
        catalogItem = getLightDriverCatalog(site).find(item => itemId === item.itemId);
    }
    if (catalogItem) {    
        return addPortTypesAvailable(catalogItem, DeviceTypes.LIGHTDRIVER) as LightDriverFamily;
    }
}

/**
 * @param itemId Accepts device record or PrimaryCatalogId as input
 * @param site 
 * @returns Heater catalog item.
 */
export const getHeaterInfo = function (itemId: (string | DeviceConfig), site?:SiteRecord): HeaterFamily {
    if (itemId && typeof itemId === "object") {
        itemId = itemId.HardwareDevice?.PrimaryCatalogId;
    }
    const indexed = getCatalogIndexed(CatalogFileNames.HeaterCatalog, site);
    let catalogItem: HeaterFamily;
    if (indexed && indexed[ itemId as string ]) {
        catalogItem = indexed[ itemId as string ];
    } else {
        catalogItem = getHeaterCatalog(site).find(hd => (hd[ 'itemId' ] ?? hd[ 'heaterID' ]) === itemId);
    }

    if (catalogItem) {
        return addPortTypesAvailable(catalogItem, DeviceTypes.HEATER) as HeaterFamily;
    }
}

export const getActuatorCatalogEntry = function(itemId: string, site?:SiteRecord):ActuatorFamily {
    const actuatorCatalog = getActuatorCatalog(site);
    const actuatorCatalogEntry = actuatorCatalog.find(item => item.itemId === itemId);
    if (actuatorCatalogEntry) {
        return addPortTypesAvailable(actuatorCatalogEntry, DeviceTypes.ACTUATORTWOWAY) as ActuatorFamily;
    }
}
/**
 * Returns Actuator Catalog entry with fixed types.
 * E.g: ControlByMcu which is stored as string in the catalog is returned as boolean.
 * @param {string} PrimaryCatalogId
 * @param {SiteRecord} site 
 * @returns {ActuatorFamilyFixed}
 */
export const getActuatorCatalogEntryFixed = function(itemId:string, site?:SiteRecord):ActuatorFamilyFixed {
    const actuatorCatalogEntry = {
        kbUrl: "https://poolside.support/kb/setting-up-and-testing-actuators/",
        ...getActuatorCatalogEntry(itemId, site)
    };
    if (actuatorCatalogEntry) {
        const actautorCatalogEntryFixed = {
            ...actuatorCatalogEntry,
            ControlByMcu: fixCatalogType(targetTypes.boolean, actuatorCatalogEntry.ControlByMcu)
        }
        return addPortTypesAvailable(actautorCatalogEntryFixed, DeviceTypes.ACTUATORTWOWAY) as ActuatorFamilyFixed;
    }
}

export const getChlorineSensorCatalogEntry = function(itemId: string): ChlorineSensorCatalogItem {
    const indexed = getCatalogIndexed(CatalogFileNames.ChlorineSensorCatalog);

    let catalogItem: ChlorineSensorCatalogItem;
    if (indexed && indexed[ itemId ]) {
        catalogItem = indexed[ itemId ];
    } else {
        catalogItem = getChlorineSensorCatalog()?.find(item => itemId === item.itemId);
    }
    if (catalogItem) {    
        return addPortTypesAvailable(catalogItem, DeviceTypes.CHLORINESENSOR) as ChlorineSensorCatalogItem;
    }
}

export const getDissolvedOxygenSensorCatalogEntry = function(itemId: string): DissolvedOxygenSensorCatalogItem {
    const indexed = getCatalogIndexed(CatalogFileNames.DissolvedOxygenSensorCatalog);

    let catalogItem: DissolvedOxygenSensorCatalogItem;
    if (indexed && indexed[ itemId ]) {
        catalogItem = indexed[ itemId ];
    } else {
        catalogItem = getDissolvedOxygenSensorCatalog()?.find(item => itemId === item.itemId);
    }
    if (catalogItem) {    
        return addPortTypesAvailable(catalogItem, DeviceTypes.DISSOLVEDOXYGENSENSOR) as DissolvedOxygenSensorCatalogItem;
    }
}

/**
 * @see getSaltCellCatalogEntryFlat()
 * @deprecated Use getSaltCellCatalogEntryFlat() instead
 */
export const getSaltCellCatalogEntry = function(itemId: string, model: string, site?:SiteRecord): SwgInfo {
    let group = getSaltCellCatalog(site).find(item => item.itemId === itemId);

    if (group) {
        group = addPortTypesAvailable(group, DeviceTypes.SALTCELL) as SwgFamily;
    }

    return {
        model: group,
        variant: group?.supportedCells.find(variant => variant.model === model)
    };
}

/**
 * Gets all data from the catalog related to a given salt cell model and cell variant.
 * 
 * @param itemId 
 * @param cellModel model prop to look up in suportedCells array.
 * @param site 
 * @returns Basic catalog data according to CatalogEntry extended by the device specific properties.
 */
export const getSaltCellCatalogEntryFlat = function(itemId: string, cellModel: string, site?:SiteRecord): SwgInfoFlat {
    let catalogEntry = getSaltCellCatalog(site).find(item => item.itemId === itemId);

    if (catalogEntry) {
        catalogEntry = addPortTypesAvailable(catalogEntry, DeviceTypes.SALTCELL) as SwgFamily;
    }
    const supportedCellEntry = catalogEntry?.supportedCells.find(item => item.model === cellModel);

    const catalogEntryCopy = JSON.parse(JSON.stringify(catalogEntry || {}));
    delete catalogEntryCopy.supportedCells;
    return {
        ...catalogEntryCopy,
        supportedCell: supportedCellEntry
    };
}

export const colorToDecimal = function(clrStr, withBrightness = false) {
    let rgbParts = [];
    if (clrStr.substring(0,1) === '#') {
        let hexClr = clrStr.replace('#', '');
        rgbParts[0] = parseInt(hexClr.substring(0, 2), 16);
        rgbParts[1] = parseInt(hexClr.substring(2, 4), 16);
        rgbParts[2] = parseInt(hexClr.substring(4, 6), 16);
        if (withBrightness && typeof rgbParts[3] !== 'undefined') {
            rgbParts[3] = parseInt(hexClr.substring(6, 8), 16);
        }
    } else {
        rgbParts = clrStr.split('|');
    }

    return rgbParts.join('|');
}

export const colorToHex = function(clrStr, withBrightness = false) {
    if (!clrStr) 
        return '#000000';
    let rgbParts = [];
    if (clrStr.substring(0,1) === '#') {
        let hexClr = clrStr.replace('#', '');
        rgbParts[0] = parseInt(hexClr.substring(0, 2), 16);
        rgbParts[1] = parseInt(hexClr.substring(2, 4), 16);
        rgbParts[2] = parseInt(hexClr.substring(4, 6), 16);
    } else {
        rgbParts = clrStr.split('|');
    }
    /*console.log('#' + 
                    Number(rgbParts[0]).toString(16).padStart(2, "0") + 
                    Number(rgbParts[1]).toString(16).padStart(2, "0") + 
                    Number(rgbParts[2]).toString(16).padStart(2, "0"));*/
    let brightness:any = '';
    if (withBrightness && rgbParts.length === 4) {
        brightness = Number(rgbParts[3]).toString(16).padStart(2, "0");
        if (isNaN(brightness)) {
            brightness = '';
        }
    }
    return '#' + 
        Number(rgbParts[0]).toString(16).padStart(2, "0") + 
        Number(rgbParts[1]).toString(16).padStart(2, "0") + 
        Number(rgbParts[2]).toString(16).padStart(2, "0") + brightness;
}

/**
 * Convert types of the catalog entry properties.
 * E.g. returns real boolean even if it was stored as string like "true" or "Y"
 * @param {targetTypes} targetType Desired/expected type of the prop e.g: boolean
 * @param value Prop as stored in the catalog. E.g: "Y"
 * @returns
 */
function fixCatalogType(targetType:targetTypes, value:any) {
    switch (targetType) {
    case targetTypes.boolean: {
        switch (value) {
        case "TRUE":
        case "Y":
        case true:
            return true;
        default:
            return false;
        }
    }
    }
}

let dlLock = {};
export class CatalogStorage {
    static versions:Record<string, AllCatalogs> = {}

    /**
     * In CatalogVersion v45 we introduced Pressure Sensor Catalogs.
     * With that, hardcoded Pressure Sensor related data was replaced by using catalog functions
     * so it is needed to emulate catalog if the Controller is below catalog version 45.
     * @returns 
     */
    static getPressureSensorCatalogMock(): PressureSensorCatalog { return {
        timestamp: "1/1/2000, 00:00:00 AM",
        version: 1000000000000,
        pressureSensors: [
            {
                "itemId": "TransducerDirect_TDWLB-LCRPPF",
                "brand": "Transducers Direct",
                "model": "TDWLB-LCRPPF",
                "description": "Transducers Direct TDWLB-LCRPPF",
                "portTypesAvailable": [ PortTypeValues.Bluetooth ],
                "supportGainControl": "",
                "notes": "",
                kbUrl: "https://poolside.support/kb/pressure-sensors/",
                "busClass": "",
                "addressUniqueType": "",
                "uiElementID": "",
                "troubleshooting": [ { } ],
                /*
                configurableFields = [ {
                    Name: Locale.gettext("Bluetooth device address"),
                    Id: "UniqueBusIdentifier",
                    Pattern: "XX:XX:XX:XX:XX:XX",
                    Regex: "/^[0-9A-F]{2}:[0-9A-F]{2}:[0-9A-F]{2}:[0-9A-F]{2}:[0-9A-F]{2}:[0-9A-F]{2}$/i"
                } ];
                */
                "configurableFields": [ {
                    "Name": "Mac Address",
                    "Id": "UniqueBusIdentifier",
                    "Pattern": "00:1a:2b:3c:4d:5e",
                    "Regex": "/^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/",
                    "Default": ""
                } ]
            }
        ]
    } }

    constructor() {
        //CatalogStorage.autoLoad(1);
        //CatalogStorage.autoLoad(2);
        //CatalogStorage.autoLoad(3);
    }

    static getVersion(version:number):AllCatalogs {
        logger.verbose("[CatalogStorage] getVersion() version:", version);
        const verStr = 'v' + version;
        if (!CatalogStorage.versions[verStr]) {
            logger.info("[CatalogStorage] getVersion() trigger autoload() version:", version);
            logger.info("[CatalogStorage] getVersion() WARNING! Missing `await` for CatalogStorage to autoload().");
            // TODO missing await
            CatalogStorage.autoLoad(version)
                .catch(err => {
                    logger.error("[CatalogStorage] getVersion() could not autoload catalog vesion:", version, err);
                });
        }
        return CatalogStorage.versions[verStr];
    }

    static getLatestVersionNumber() {
        const versionStrings = Object.keys(LocalCatalogs).map(path => path.split('/')[0]);

        return versionStrings
            .map(vStr => Number(vStr.replace('v', '')))
            .sort((a, b) => b - a)[0]
        ;
    }

    static async autoLoad(version:number):Promise<void> {
        logger.info("[CatalogStorage] autoLoad() version:", version);
        const verStr = 'v' + version;
        if (!LocalCatalogs[verStr + '/ActuatorCatalog.json']) {
            logger.info('[CatalogStorage] Cannot find version: ' + version + ' of catalogs locally, loading / downloading...');
            await this.downloadVersion(version);
        } else {
            const ac = new AllCatalogs();
            ac.Actuator = LocalCatalogs[ verStr + '/' +  CatalogFileNames.ActuatorCatalog ].data;
            ac.Blower = LocalCatalogs[ verStr + '/' + CatalogFileNames.BlowerCatalog ].data;
            if (version >= 33) {
                ac.ChlorineSensor = LocalCatalogs[ verStr + '/' + CatalogFileNames.ChlorineSensorCatalog ].data;
            }
            if (version >= 44) {
                ac.DissolvedOxygenSensor = LocalCatalogs[ verStr + '/' + CatalogFileNames.DissolvedOxygenSensorCatalog ].data;
            }
            if (version >= 26) {
                ac.FlowMeter = LocalCatalogs[ verStr + '/' +  CatalogFileNames.FlowMeterCatalog ].data;
            }
            ac.Heater = LocalCatalogs[ verStr + '/' + CatalogFileNames.HeaterCatalog ].data;
            ac.Light = LocalCatalogs[ verStr + '/' + CatalogFileNames.LightCatalog ].data;
            if (version >= 23) {
                ac.LightDriver = LocalCatalogs[ verStr + '/' + CatalogFileNames.LightDriverCatalog ].data;
            }

            if (version >= 45) {
                ac.PressureSensor = LocalCatalogs[ verStr + '/' + CatalogFileNames.PressureSensorCatalog ].data;
            } else {
                ac.PressureSensor = CatalogStorage.getPressureSensorCatalogMock();
            }
            
            ac.Pump = LocalCatalogs[ verStr + '/' + CatalogFileNames.PumpCatalog ].data;
            if (version >= 21) {
                ac.Remote = LocalCatalogs[ verStr + '/' + CatalogFileNames.RemoteCatalog ].data;
            }
            ac.SaltChlorinator = LocalCatalogs[ verStr + '/' + CatalogFileNames.SwgCatalog ].data;
            
            CatalogStorage.versions[ verStr ] = ac;
        }
    }

    static async downloadVersion(version:number):Promise<AllCatalogs> {
        if (dlLock[version]) {
            return;
        }
        dlLock[version] = true;
        let verStr = 'v' + version;

        let ac = new AllCatalogs();

        ac.Actuator = (await checkDataFile(verStr + '/' + CatalogFileNames.ActuatorCatalog, 0)) as ActuatorCatalog;
        logger.debug('Actuator catalog loaded with ' + ac.Actuator?.actuators?.length + ' items');

        ac.Blower = (await checkDataFile(verStr + '/' + CatalogFileNames.BlowerCatalog, 0)) as BlowerCatalog;
        logger.debug('Blower catalog loaded with ' + ac.Blower?.blowers?.length + ' items');

        if (version >= 33) {
            ac.ChlorineSensor = (await checkDataFile(verStr + '/' + CatalogFileNames.ChlorineSensorCatalog, 0)) as ChlorineSensorCatalog;
            logger.debug('ChlorineSensor catalog loaded with ' + ac.ChlorineSensor?.chlorineSensors?.length + ' items');
        }

        if (version >= 44) {
            ac.DissolvedOxygenSensor = (await checkDataFile(verStr + '/' + CatalogFileNames.DissolvedOxygenSensorCatalog, 0)) as DissolvedOxygenSensorCatalog;
            logger.debug('DissolvedOxygenSensor catalog loaded with ' + ac.DissolvedOxygenSensor?.DissolvedOxygenSensors?.length + ' items');
        }

        ac.Heater = (await checkDataFile(verStr + '/' + CatalogFileNames.HeaterCatalog, 0)) as HeaterCatalog;
        logger.debug('Heater catalog loaded with ' + ac.Heater?.heaters?.length + ' items');

        if (version >= 26) {
            ac.FlowMeter = (await checkDataFile(verStr + '/' + CatalogFileNames.FlowMeterCatalog, 0)) as FlowMeterCatalog;
            logger.debug('Flow Meter catalog loaded with ' + ac?.FlowMeter?.flowmeters?.length + ' items');
        }

        ac.Light = (await checkDataFile(verStr + '/' + CatalogFileNames.LightCatalog, 0)) as LightCatalog;
        logger.debug('Light catalog loaded with ' + ac?.Light?.brands?.length + ' items');
        
        if (version >= 23) {
            ac.LightDriver = (await checkDataFile(verStr + '/' + CatalogFileNames.LightDriverCatalog, 0)) as LightDriverCatalog;
            logger.debug('Light Driver catalog loaded with ' + ac?.LightDriver?.lightdrivers?.length + ' items');
        }

        if (version >= 45) {
            ac.PressureSensor = (await checkDataFile(verStr + '/' + CatalogFileNames.PressureSensorCatalog, 0)) as PressureSensorCatalog;
            logger.debug('PressureSensor catalog loaded with ' + ac.PressureSensor?.pressureSensors?.length + ' items');
        } else {
            ac.PressureSensor = CatalogStorage.getPressureSensorCatalogMock();
        }

        ac.Pump = (await checkDataFile(verStr + '/' + CatalogFileNames.PumpCatalog, 0)) as PumpCatalog;
        logger.debug('Pump catalog loaded with ' + ac.Pump?.pumps?.length + ' items');

        if (version >= 21) {
            ac.Remote = (await checkDataFile(verStr + '/' + CatalogFileNames.RemoteCatalog, 0)) as RemoteCatalog;
            logger.debug('Remote catalog loaded with ' + ac.Remote?.remotes?.length + ' items');
        }
        
        ac.SaltChlorinator = (await checkDataFile(verStr + '/' + CatalogFileNames.SwgCatalog, 0)) as SwgCatalog;
        logger.debug('SaltChlorinator catalog loaded with ' + ac.SaltChlorinator?.saltWaterChlorinators?.length + ' items');
        

        CatalogStorage.versions[verStr] = ac;
        delete dlLock[version];

        return ac;
    }
}

export function getChlorineSensorRangeLocalized(item: ChloirneSensorGains): string {
    const items = {
        [ ChloirneSensorGains.Auto ]: Locale.gettext("Automatic Range Selection"),
        [ ChloirneSensorGains.ZERO_TO_160_PPM ]: Locale.gettext("0 - 160 PPM"),
        [ ChloirneSensorGains.ZERO_TO_80_PPM ] : Locale.gettext("0 - 80 PPM"),
        [ ChloirneSensorGains.ZERO_TO_40_PPM ] : Locale.gettext("0 - 40 PPM"),
        [ ChloirneSensorGains.ZERO_TO_20_PPM ] : Locale.gettext("0 - 20 PPM"),
        [ ChloirneSensorGains.ZERO_TO_10_PPM ] : Locale.gettext("0 - 10 PPM"),
        [ ChloirneSensorGains.ZERO_TO_5_PPM ] : Locale.gettext("0 - 5 PPM"),
        [ ChloirneSensorGains.ZERO_TO_2_PPM ] : Locale.gettext("0 - 2 PPM"),
        [ ChloirneSensorGains.ZERO_TO_1_PPM ] : Locale.gettext("0 - 1 PPM"),
    }
    return items[ item ] ?? item;
}

export function getCatalogItemTaintsLocalized(item: CatalogItemTaints): string {
    const items = {
        [ CatalogItemTaints.OneWayCommunication ]: Locale.gettext("This device only supports one-way communication. Poolside cannot read its actual status or detect faults, and must operate on estimates instead of real readings. Without true status feedback, errors may go unnoticed and equipment may run in unintended conditions. For more reliable operation consider using a device that supports two-way communication."),
    }
    return items[ item ] ?? item;
}
